import React, { useEffect, useState } from 'react';
import {
  CheckCircle2, EyeOff, KeyRound, Link2, Loader2, PlugZap,
  Save, ShieldCheck, Trash2, XCircle,
} from 'lucide-react';
import { COLORS } from '../../data/mockData';
import {
  fetchAdminIntegrations, testAdminIntegration, updateAdminIntegration,
} from '../../services/api';
import { adminInputStyle as inputStyle, adminLabelStyle as labelStyle } from './AdminModal';

const SECRET_FIELDS = {
  openai: [{ key: 'api_key', label: 'OpenAI API Key', placeholder: 'sk-…' }],
  stripe: [
    { key: 'api_key', label: 'Stripe Secret Key', placeholder: 'sk_live_… or sk_test_…' },
    { key: 'webhook_secret', label: 'Webhook Signing Secret', placeholder: 'whsec_…' },
  ],
  sendgrid: [{ key: 'api_key', label: 'SendGrid API Key', placeholder: 'SG.…' }],
};

function ProviderCard({ initial, onUpdated }) {
  const [form, setForm] = useState({
    api_url: initial.api_url || '',
    from_email: initial.from_email || '',
    api_key: '',
    webhook_secret: '',
  });
  const [clearSecrets, setClearSecrets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);

  const save = async () => {
    setSaving(true); setMessage(null);
    try {
      const payload = {
        api_url: form.api_url,
        clear_secrets: clearSecrets,
      };
      if (initial.provider === 'sendgrid') payload.from_email = form.from_email;
      if (form.api_key.trim()) payload.api_key = form.api_key.trim();
      if (form.webhook_secret.trim()) payload.webhook_secret = form.webhook_secret.trim();
      const updated = await updateAdminIntegration(initial.provider, payload);
      setForm(p => ({ ...p, api_key: '', webhook_secret: '' }));
      setClearSecrets([]);
      setMessage({ ok: true, text: 'Saved securely. Changes are live now.' });
      onUpdated(updated);
    } catch (e) {
      setMessage({ ok: false, text: e?.response?.data?.detail || 'Save failed' });
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setMessage(null);
    try {
      await testAdminIntegration(initial.provider);
      setMessage({ ok: true, text: 'Connection successful.' });
    } catch (e) {
      setMessage({ ok: false, text: e?.response?.data?.detail || 'Connection failed' });
    } finally { setTesting(false); }
  };

  const toggleClear = key => {
    setClearSecrets(old => old.includes(key) ? old.filter(x => x !== key) : [...old, key]);
  };

  return (
    <div data-testid={`integration-${initial.provider}`} style={{
      backgroundColor: COLORS.surface, borderRadius: 16, padding: 22,
      border: `1px solid ${COLORS.border}`, boxShadow: '0 2px 8px rgba(0,0,0,.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, backgroundColor: `${COLORS.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PlugZap size={21} color={COLORS.primary} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{initial.label}</h3>
            <span style={{ fontSize: 11, color: COLORS.textSecondary }}>Runtime configuration</span>
          </div>
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999,
          padding: '5px 9px', fontSize: 11, fontWeight: 700,
          color: initial.api_key_configured ? COLORS.success : COLORS.textSecondary,
          backgroundColor: initial.api_key_configured ? `${COLORS.success}14` : COLORS.surfaceSecondary,
        }}>
          {initial.api_key_configured ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          {initial.api_key_configured ? 'Key configured' : 'No key'}
        </span>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}><Link2 size={13} style={{ display: 'inline', marginRight: 5 }} />API URL</label>
        <input data-testid={`${initial.provider}-api-url`} value={form.api_url}
          onChange={e => setForm({ ...form, api_url: e.target.value })} style={inputStyle} />
      </div>

      {initial.provider === 'sendgrid' && (
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Verified From Email</label>
          <input type="email" value={form.from_email}
            onChange={e => setForm({ ...form, from_email: e.target.value })} style={inputStyle} />
        </div>
      )}

      {(SECRET_FIELDS[initial.provider] || []).map(secret => {
        const configured = initial[`${secret.key}_configured`];
        const clear = clearSecrets.includes(secret.key);
        return (
          <div key={secret.key} style={{ marginBottom: 14 }}>
            <label style={labelStyle}><KeyRound size={13} style={{ display: 'inline', marginRight: 5 }} />{secret.label}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="password" autoComplete="new-password"
                data-testid={`${initial.provider}-${secret.key}`}
                value={form[secret.key]} placeholder={clear ? 'Will be removed on save' : (configured ? `${initial[`${secret.key}_hint`]} — enter only to replace` : secret.placeholder)}
                disabled={clear}
                onChange={e => setForm({ ...form, [secret.key]: e.target.value })}
                style={{ ...inputStyle, flex: 1, opacity: clear ? .55 : 1 }} />
              {configured && (
                <button type="button" onClick={() => toggleClear(secret.key)}
                  title={clear ? 'Keep existing secret' : 'Remove secret on save'}
                  style={{ width: 42, borderRadius: 10, border: `1px solid ${clear ? COLORS.error : COLORS.border}`, background: clear ? `${COLORS.error}10` : '#FFF', color: clear ? COLORS.error : COLORS.textSecondary, cursor: 'pointer' }}>
                  {clear ? <EyeOff size={16} /> : <Trash2 size={16} />}
                </button>
              )}
            </div>
            <div style={{ marginTop: 5, fontSize: 11, color: COLORS.textSecondary }}>
              Stored values are never displayed; a blank field preserves the current secret.
            </div>
          </div>
        );
      })}

      {message && (
        <div style={{ padding: '9px 11px', borderRadius: 9, marginBottom: 12, fontSize: 12, color: message.ok ? COLORS.success : COLORS.error, backgroundColor: `${message.ok ? COLORS.success : COLORS.error}0D` }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} data-testid={`save-${initial.provider}`}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: 11, borderRadius: 10, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontWeight: 700, cursor: 'pointer' }}>
          {saving ? <Loader2 size={15} className="spin" /> : <Save size={15} />} Save
        </button>
        <button onClick={test} disabled={testing || !initial.api_key_configured}
          data-testid={`test-${initial.provider}`}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: 11, borderRadius: 10, border: `1px solid ${COLORS.primary}`, backgroundColor: '#FFF', color: COLORS.primary, fontWeight: 700, cursor: initial.api_key_configured ? 'pointer' : 'not-allowed', opacity: initial.api_key_configured ? 1 : .5 }}>
          {testing ? <Loader2 size={15} className="spin" /> : <PlugZap size={15} />} Test
        </button>
      </div>
    </div>
  );
}

export default function IntegrationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAdminIntegrations()
      .then(d => setItems(d.integrations || []))
      .catch(e => setError(e?.response?.data?.detail || 'Could not load integrations'))
      .finally(() => setLoading(false));
  }, []);

  const updateItem = item => setItems(old => old.map(x => x.provider === item.provider ? item : x));

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="spin" color={COLORS.primary} /></div>;
  if (error) return <p style={{ color: COLORS.error }}>{error}</p>;

  return (
    <div data-testid="admin-integrations">
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 16, marginBottom: 18, borderRadius: 12, backgroundColor: '#FFF8E1', border: '1px solid #F0E0A0' }}>
        <ShieldCheck size={20} color="#A65500" style={{ flexShrink: 0 }} />
        <div style={{ fontSize: 13, lineHeight: 1.55, color: COLORS.textPrimary }}>
          <strong>Secrets are encrypted with AES-256-GCM before database storage.</strong>{' '}
          Existing values are masked and are never returned to the browser or written to logs.
          API URLs must use HTTPS and cannot point to local or private-network addresses.
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        {items.map(item => <ProviderCard key={item.provider} initial={item} onUpdated={updateItem} />)}
      </div>
    </div>
  );
}
